<template>
  <div class="skill-groups-list">
    <div class="toolbar">
      <input
        v-model="search"
        @input="debouncedFilter"
        type="text"
        class="filter-input"
        :placeholder="$t('bo.esco.searchGroups')"
      />
      <select v-model="statusFilter" class="filter-select">
        <option value="all">{{ $t('bo.esco.allStatuses') }}</option>
        <option value="active">{{ $t('bo.esco.activeOnly') }}</option>
        <option value="archived">{{ $t('bo.esco.fullyArchived') }}</option>
      </select>
      <select v-model="categoryFilter" class="filter-select">
        <option :value="null">{{ $t('bo.esco.allCategories') }}</option>
        <option value="domain">{{ $t('bo.platformSkills.domain') }}</option>
        <option value="transversal">{{ $t('bo.platformSkills.transversal') }}</option>
        <option value="__none__">{{ $t('bo.esco.noType') }}</option>
      </select>
      <span class="toolbar-info">
        {{ $t('bo.esco.toolbarInfo', { groups: filteredGroups.length, skills: totalActiveChildren }) }}
        <span v-if="totalMergedChildren > 0"> · {{ $t('bo.esco.mergedCount', { count: totalMergedChildren }) }}</span>
        <span v-if="totalArchivedChildren > 0"> · {{ $t('bo.esco.archivedCount', { count: totalArchivedChildren }) }}</span>
      </span>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.esco.loadingSkillGroups') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="filteredGroups.length === 0" class="empty">
      {{ $t('bo.esco.noSkillGroupsFound') }}
      <span v-if="rawSkills.length === 0">{{ $t('bo.esco.noSkillGroupsHint') }}</span>
    </div>

    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.esco.colParentSkill') }}</th>
          <th>{{ $t('bo.esco.colType') }}</th>
          <th>{{ $t('bo.esco.colActiveSkills') }}</th>
          <th>{{ $t('bo.esco.colMerged') }}</th>
          <th>{{ $t('bo.common.archived') }}</th>
          <th>{{ $t('bo.esco.colTypes') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="group in filteredGroups" :key="group.group_key">
          <!-- Group header row -->
          <tr class="group-row" @click="toggleExpand(group.group_key)">
            <td class="col-name">
              <span class="expand-icon">{{ expandedIds.has(group.group_key) ? '▾' : '▸' }}</span>
              <span class="group-title">{{ group.parent_title }}</span>
            </td>
            <td>
              <span v-if="group.group_type" :class="['badge-type', `badge-type-${group.group_type}`]">{{ group.group_type }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td><span class="count-badge count-active">{{ group.activeCount }}</span></td>
            <td>
              <span v-if="group.mergedCount > 0" class="count-badge count-merged">{{ group.mergedCount }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span v-if="group.archivedCount > 0" class="count-badge count-archived">{{ group.archivedCount }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span v-for="type in group.types" :key="type" :class="['badge-type', `badge-type-${type}`]">
                {{ type }}
              </span>
            </td>
            <td class="col-actions" @click.stop>
              <button @click="openEdit(group)" class="btn-edit">{{ $t('bo.common.edit') }}</button>
              <button
                @click="archiveGroup(group)"
                :disabled="archivingId === group.group_key"
                class="btn-archive"
                :title="$t('bo.esco.archiveAllInGroup')"
              >{{ archivingId === group.group_key ? '…' : $t('bo.common.archive') }}</button>
            </td>
          </tr>

          <!-- Expanded: child skills (lazy-loaded) -->
          <template v-if="expandedIds.has(group.group_key)">
            <tr v-if="groupSkillsCache[group.group_key]?.loading" class="child-row">
              <td colspan="7" class="loading-inline">{{ $t('bo.esco.loadingSkills') }}</td>
            </tr>
            <tr
              v-for="skill in group.skills"
              :key="skill.id"
              :class="['child-row', { 'child-row--merged': !skill.is_active }]"
            >
              <td class="child-name">
                <span class="child-indent">└</span>
                {{ skill.title }}
                <span v-if="!skill.is_active" :class="skill.parent_id ? 'badge-merged' : 'badge-archived'">{{ skill.parent_id ? 'merged' : 'archived' }}</span>
              </td>
              <td colspan="3">
                <span :class="['badge-type', `badge-type-${skill.esco_skill_type}`]">{{ skill.esco_skill_type }}</span>
              </td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  <!-- Edit modal -->
  <EditSkillGroupModal
    v-if="editingGroup"
    :group="editingGroup"
    @close="editingGroup = null"
    @saved="handleSaved"
  />
</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()
import EditSkillGroupModal from './EditSkillGroupModal.vue'

// ── State ──────────────────────────────────────────────────────────────────
const groupList = ref([])
const groupSkillsCache = ref({})
const loading = ref(false)
const error = ref('')
const search = ref('')
const statusFilter = ref('all')
const categoryFilter = ref(null)
const expandedIds = ref(new Set())
const archivingId = ref(null)
const editingGroup = ref(null)

let debounceTimer = null
const debouncedFilter = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {}, 0)
}

// ── Computed ───────────────────────────────────────────────────────────────
const groups = computed(() =>
  groupList.value.map(g => ({
    group_key: g.group_key,
    parent_title: g.display_name ?? g.group_key,
    activeCount: g.active_count,
    mergedCount: g.merged_count,
    archivedCount: g.archived_count,
    types: g.types,
    group_type: g.group_type ?? null,
    skills: groupSkillsCache.value[g.group_key]?.skills ?? [],
  })).sort((a, b) => a.parent_title.localeCompare(b.parent_title))
)

const filteredGroups = computed(() => {
  let list = groups.value
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(g => g.parent_title.toLowerCase().includes(q))
  }
  if (statusFilter.value === 'active') {
    list = list.filter(g => g.activeCount > 0)
  } else if (statusFilter.value === 'archived') {
    list = list.filter(g => g.activeCount === 0 && g.archivedCount > 0)
  }
  if (categoryFilter.value === '__none__') {
    list = list.filter(g => !g.group_type)
  } else if (categoryFilter.value !== null) {
    list = list.filter(g => g.group_type === categoryFilter.value)
  }
  return list
})

const totalActiveChildren = computed(() => groupList.value.reduce((s, g) => s + g.active_count, 0))
const totalMergedChildren = computed(() => groupList.value.reduce((s, g) => s + g.merged_count, 0))
const totalArchivedChildren = computed(() => groupList.value.reduce((s, g) => s + (g.archived_count ?? 0), 0))

// ── Load groups (fast — single DB query) ──────────────────────────────────
const loadGroups = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await escoService.getSkillGroupsList()
    groupList.value = res.data || []
  } catch {
    error.value = t('bo.esco.failedLoadSkillGroups')
  } finally {
    loading.value = false
  }
}

// ── Lazy-load skills for a group (on expand) ──────────────────────────────
const loadGroupSkillsIfNeeded = async (groupKey) => {
  if (groupSkillsCache.value[groupKey]?.loaded) return

  if (!groupSkillsCache.value[groupKey]) {
    groupSkillsCache.value[groupKey] = { skills: [], loading: true, error: null, loaded: false }
  } else {
    groupSkillsCache.value[groupKey].loading = true
  }

  try {
    const res = await escoService.getGroupSkills(groupKey, { per_page: 100 })
    // Handle pagination: if more pages, load them all
    const firstPage = res.data?.data || []
    const total = res.data?.total || 0
    let allSkills = firstPage

    if (total > 100) {
      const totalPages = Math.ceil(total / 100)
      const extras = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          escoService.getGroupSkills(groupKey, { per_page: 100, page: i + 2 })
        )
      )
      for (const r of extras) {
        allSkills = allSkills.concat(r.data?.data || [])
      }
    }

    groupSkillsCache.value[groupKey] = { skills: allSkills, loading: false, error: null, loaded: true }
  } catch {
    groupSkillsCache.value[groupKey] = { skills: [], loading: false, error: 'Failed to load', loaded: false }
  }
}

// ── Toggle expand — triggers lazy load ────────────────────────────────────
const toggleExpand = (pid) => {
  const next = new Set(expandedIds.value)
  if (next.has(pid)) {
    next.delete(pid)
  } else {
    next.add(pid)
    loadGroupSkillsIfNeeded(pid)
  }
  expandedIds.value = next
}

// ── Edit modal ─────────────────────────────────────────────────────────────
const openEdit = (group) => {
  editingGroup.value = group
}

const handleSaved = (updated) => {
  const idx = groupList.value.findIndex(g => g.group_key === editingGroup.value.group_key)
  if (idx !== -1) {
    const oldKey = groupList.value[idx].group_key
    groupList.value[idx] = {
      ...groupList.value[idx],
      group_key: updated.group_key,
      display_name: updated.name,
    }
    // If renamed, move skills cache to new key
    if (oldKey !== updated.group_key && groupSkillsCache.value[oldKey]) {
      groupSkillsCache.value[updated.group_key] = groupSkillsCache.value[oldKey]
      delete groupSkillsCache.value[oldKey]
    }
  }
  editingGroup.value = null
}

// ── Archive ────────────────────────────────────────────────────────────────
const archiveGroup = async (group) => {
  if (!confirm(t('bo.esco.confirmArchiveGroup', { count: group.activeCount, name: group.parent_title }))) return
  archivingId.value = group.group_key
  error.value = ''
  try {
    await escoService.archiveSkillGroup(group.group_key)
    await loadGroups()
    // Invalidate cached skills for this group
    delete groupSkillsCache.value[group.group_key]
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.failedArchiveGroup')
  } finally {
    archivingId.value = null
  }
}

const rawSkills = computed(() =>
  Object.values(groupSkillsCache.value).flatMap(c => c.skills ?? [])
)

onMounted(loadGroups)
</script>

<style scoped>
.skill-groups-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-width: 220px;
}
.filter-input:focus { outline: none; border-color: #3b82f6; }

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}
.filter-select:focus { outline: none; border-color: #3b82f6; }

.toolbar-info { font-size: 0.8rem; color: #6b7280; margin-left: auto; }

.loading, .empty { padding: 3rem; text-align: center; color: #6b7280; }
.error-msg { padding: 1rem 1.25rem; color: #dc2626; }

.table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.group-row {
  cursor: pointer;
  background: #fafafa;
}
.group-row:hover td { background: #f0f9ff; }
.group-row td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
}

.child-row td {
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.825rem;
  color: #374151;
}
.child-row--merged td { color: #9ca3af; background: #fafafa; }

.col-name { display: flex; align-items: center; gap: 0.5rem; }
.col-actions { display: flex; gap: 0.5rem; align-items: center; }

.expand-icon { font-size: 0.75rem; color: #6b7280; width: 1rem; }
.group-title { color: #111827; }

.child-name { display: flex; align-items: center; gap: 0.5rem; }
.child-indent { color: #9ca3af; font-size: 0.9rem; }

.count-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.count-active { background: #d1fae5; color: #065f46; }
.count-merged { background: #fef3c7; color: #92400e; }
.count-archived { background: #fee2e2; color: #991b1b; }

.badge-merged {
  font-size: 0.7rem;
  background: #fef3c7;
  color: #92400e;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-weight: 600;
}

.badge-archived {
  font-size: 0.7rem;
  background: #fee2e2;
  color: #991b1b;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-weight: 600;
}

.badge-type {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  margin-right: 0.2rem;
}
.badge-type-skill { background: #dbeafe; color: #1e40af; }
.badge-type-knowledge { background: #d1fae5; color: #065f46; }
.badge-type-attitude { background: #fce7f3; color: #9d174d; }

.text-muted { color: #9ca3af; font-size: 0.8rem; }

.cat-badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 600;
  border: 1px solid;
  white-space: nowrap;
}
.inline-input {
  padding: 0.3rem 0.5rem;
  border: 1px solid #3b82f6;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-width: 200px;
  outline: none;
}

.btn-edit {
  padding: 0.25rem 0.6rem;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
}
.btn-edit:hover { background: #eff6ff; }

.btn-save {
  padding: 0.25rem 0.6rem;
  border: none;
  background: #3b82f6;
  color: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
}
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-cancel-sm {
  padding: 0.25rem 0.6rem;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
}

.btn-archive {
  padding: 0.25rem 0.6rem;
  border: 1px solid #f59e0b;
  color: #b45309;
  background: white;
  border-radius: 0.375rem;
  font-size: 0.78rem;
  cursor: pointer;
}
.btn-archive:hover { background: #fffbeb; }
.btn-archive:disabled { opacity: 0.5; cursor: not-allowed; }
.loading-inline { padding: 0.5rem 1rem; color: #9ca3af; font-size: 0.82rem; font-style: italic; }
</style>
